import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { isEmailFormatValid, isFieldFilled, isUsernameValid, validateRequiredField } from '@src/util/validation'
import { EditUserDto } from '@src/dto/edit-user-dto'
import User from '@src/data/user'
import { snakecaseKeys } from '@src/util/functions'
import camelcaseKeys from 'camelcase-keys'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { ContentBox } from '@src/elements/content-box/content-box'
import { InputFootnote } from '@src/elements/input/input-footnote'

type ProfileResponse = { profile: User }

@Component({
  computed: {
    initialUser () {
      return this.$store.state.user
    }
  }
})
export default class TrainerPersonalInformation extends Vue {

  protected initialUser: User
  protected user: EditUserDto = new EditUserDto()
  protected fieldErrors: string[] = []
  protected apiClientProfile: ApiClient
  protected apiClientEmail: ApiClient

  constructor () {
    super()
    this.apiClientProfile = new ApiClient(API.TRAINERS_API.PROFILE)
    this.apiClientEmail = new ApiClient(API.TRAINERS_API.EMAIL)
  }

  beforeMount () {
    this.user = new EditUserDto(this.initialUser)
    this.$watch('initialUser', () => {
      this.setUpdatedProfile()
    })
    return this.getUpdatedProfile()
  }

  setUpdatedProfile () {
    if (this.fieldErrors.indexOf('email_duplicate') >= 0) {
      this.user = new EditUserDto({ ...this.initialUser, email: this.user.email })
    } else {
      this.user = new EditUserDto(this.initialUser)
    }
  }

  getUpdatedProfile () {
    return this.apiClientProfile.get<ProfileResponse>()
      .then(response => {
        let user = new User(camelcaseKeys(response.profile, { deep: true }))
        this.$store.dispatch('setUser', user)
      })
      .catch((err) => {
        console.log('can not get updated trainer profile', err)
      })
  }

  save () {
    this.validateForm()
    if (this.fieldErrors.length > 0) {
      this.scrollError()
    }

    if (this.fieldErrors.length === 0) {
      this.updateProfile()
    }
  }

  updateProfile () {
    this.apiClientProfile.put<ProfileResponse>('', { profile: snakecaseKeys(this.user) }).then((response) => {

      if (isFieldFilled(this.user.email) && this.user.email !== this.initialUser.email) {
        return this.updateEmail()
      } else {
        let user = new User(camelcaseKeys(response.profile, { deep: true }))
        this.$store.dispatch('setUser', user)
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.ACCOUNT.PERSONAL_INFORMATION_SAVED'), fadeOut: true })
      }
    }).catch((error) => {
      console.log('error saving profile updates', error)
      if (error.status === 422 && error.errorData.errors && error.errorData.errors.username && error.errorData.errors.username.length > 0) {
        this.fieldErrors.push('username_duplicate')
      }
      if (error.status === 422 && error.errorData.errors && error.errorData.errors.base && error.errorData.errors.base.includes('username')) {
        this.fieldErrors.push('username')
      }
    })
  }

  updateEmail () {
    return this.apiClientEmail.put('', { email: this.user.email })
      .then(() => this.$store.dispatch('showToast', {
        message: `${this.$t('PAGE.MEMBERS.ACCOUNT.UNCONFIRMED_EMAIL.TITLE')} ${this.$t('PAGE.MEMBERS.ACCOUNT.UNCONFIRMED_EMAIL.MESSAGE')} ${this.user.email}`,
        fadeOut: false
      }))
      .catch((error) => {
        console.log('error updating email in profile settings', error)
        if (error.status === 409) {
          this.fieldErrors.push('email_duplicate')
        }
      })
      .finally(() => {
        return this.getUpdatedProfile()
      })
  }

  validateForm () {
    let fieldErrors: string[] = []
    if (validateRequiredField(this.user.firstName)) {
      fieldErrors.push('firstName')
    }
    if (validateRequiredField(this.user.lastName)) {
      fieldErrors.push('lastName')
    }
    if (this.user.username.length < 5) {
      fieldErrors.push('username_too_short')
    }
    if (!isUsernameValid(this.user.username)) {
      fieldErrors.push('username')
    }
    if (isFieldFilled(this.user.email) && !isEmailFormatValid(this.user.email)) {
      fieldErrors.push('email')
    }
    if (validateRequiredField(this.user.displayName)) {
      fieldErrors.push('displayName')
    }
    this.fieldErrors = fieldErrors
  }

  reset () {
    this.user = new EditUserDto(this.initialUser)
    this.$children.forEach(child => child.$emit('resetValue'))
    this.$forceUpdate()
    this.fieldErrors = []
  }

  resendEmail () {
    if (this.initialUser.unconfirmedEmail && this.initialUser.unconfirmedEmail !== this.initialUser.email) {
      this.apiClientEmail.put('', { email: this.initialUser.unconfirmedEmail })
        .then(() => {
          // reset email and email errors
          this.fieldErrors.splice(this.fieldErrors.indexOf('email'), 1)
          this.user.email = this.initialUser.email
        })
        .catch((error) => {
          console.log('error updating email in profile settings', error)
          if (error.status === 409) {
            this.user.email = this.initialUser.unconfirmedEmail
            this.fieldErrors.push('email_duplicate')
          }
        })
    }
  }

  setUsername (value) {
    this.user.username = value.replace(/\s+/g, '')
  }

  scrollError () {
    let container = document.querySelector('.scroll-content')
    if (container) {
      let firstErrorInput = container.querySelector('.personal-information')
      if (firstErrorInput) {
        container.scrollTop = firstErrorInput.getBoundingClientRect().top
      }
    }
  }

  render (h: CreateElement) {

    const isEmailUnconfirmed = this.initialUser.unconfirmedEmail && this.initialUser.unconfirmedEmail !== this.initialUser.email && this.fieldErrors.indexOf('email') < 0 &&
      this.fieldErrors.indexOf('email_duplicate') < 0

    return <div class='personal-information' direction='column'>
      <Subtitle1>{this.$t('PAGE.MEMBERS.ACCOUNT.PERSONAL_INFORMATION')}</Subtitle1>

      <ContentBox>
        <div slot='content'>
          <div class='row'>
            <Input ref='firstName' class={`short ${this.fieldErrors.indexOf('firstName') >= 0 ? 'error' : ''}`} value={this.user.firstName}
                   label={`${this.$t('FIRST_NAME')}*`} valueChanged={(value) => this.user.firstName = value} />
            <Input ref='lastName' class={`short ${this.fieldErrors.indexOf('lastName') >= 0 ? 'error' : ''}`} value={this.user.lastName}
                   label={`${this.$t('LAST_NAME')}*`} valueChanged={(value) => this.user.lastName = value} />
          </div>

          <div class='row'>
            <div class='short'>
              <Input ref='username' class={this.fieldErrors.find((u) => u.includes('username')) ? 'error' : ''}
                     value={this.user.username} valueChanged={this.setUsername} label={`${this.$t('USERNAME')}*`} />
              <InputFootnote class='error'>{this.fieldErrors.includes('username_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_USERNAME') : null}</InputFootnote>
              {this.fieldErrors.includes('username_too_short') ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_TOO_SHORT')}</InputFootnote> : ''}
              {this.fieldErrors.includes('username') ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_CHARACTERS_ALLOWED')}</InputFootnote> : '' }
            </div>
            <div class='short'>
              <Input ref='displayName' class={`${this.fieldErrors.indexOf('displayName') >= 0 ? 'error' : ''}`} value={this.user.displayName}
                     valueChanged={(value) => this.user.displayName = value} label={`${this.$t('PAGE.MEMBERS.ACCOUNT.DISPLAY_NAME')}*`} />
            </div>
          </div>

          <div class='row'>
            <div class='short'>
              <Input ref='email' class={`${this.fieldErrors.indexOf('email') >= 0 || this.fieldErrors.indexOf('email_duplicate') >= 0 ? 'error' : ''}`} value={this.user.email}
                     valueChanged={(value) => this.user.email = value} label={this.$t('PAGE.MEMBERS.ACCOUNT.EMAIL_ADDRESS')} />
              <InputFootnote class='error'>{this.fieldErrors.includes('email') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.INVALID_EMAIL.MESSAGE') : null}</InputFootnote>
              <InputFootnote class='error'>{this.fieldErrors.includes('email_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_EMAIL.MESSAGE') : null}</InputFootnote>
              {isEmailUnconfirmed ? <InputFootnote>
                  {this.$t('PAGE.MEMBERS.ACCOUNT.UNCONFIRMED_EMAIL.TITLE') + '\n' + this.$t('PAGE.MEMBERS.ACCOUNT.UNCONFIRMED_EMAIL.MESSAGE')}<b> {this.initialUser.unconfirmedEmail}</b>
              </InputFootnote>
                : <InputFootnote>{this.$t('PAGE.MEMBERS.ACCOUNT.EMAIL_REQUIREMENTS_TRAINER')}</InputFootnote>}
            </div>
          </div>

          <div class='row'>
            <Input ref='contactNumber' class='short' value={this.user.contactNumber} valueChanged={(value) => this.user.contactNumber = value}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.CONTACT_NUMBER')} />
          </div>
        </div>

        <div class='btn-row' slot='bottom'>
          <Button ref='resendBtn' class='resend-btn' type='outline' label={this.$t('PAGE.MEMBERS.ACCOUNT.RESEND_CONFIRMATION_EMAIL')} click={this.resendEmail} />
          <Button ref='resetBtn' class='reset-btn' type='outline' label={this.$t('RESET')} click={this.reset} />
          <Button ref='saveBtn' class='save-btn' type='elevated' label={this.$t('PAGE.MEMBERS.ACCOUNT.UPDATE')} click={this.save} />
        </div>
      </ContentBox>
    </div>
  }
}
